import React, { useState, useEffect } from 'react';

import { css, StyleSheet } from 'aphrodite';
import { useParams } from 'react-router-dom';
import { Spinner, Table } from 'react-bootstrap';

import { ANNUALIZED_MFR_GMV_PRODUCT_MARKET_SHARE } from 'lib/networking/endpoints';
import {
  bodyText,
  borderGray,
  ghostWhite,
  softGreen,
  softPurple,
  warmGray,
  white,
} from 'lib/css/colors';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import useGet from 'lib/hooks/useGet';
import PaginationComponent from 'components/shared/PaginationComponent';

function MarketShareView() {
  const { productId } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Reset page when product changes
  useEffect(() => {
    setCurrentPage(1);
  }, [productId]);

  // Calculate offset, ensuring we're on page 1 if we would exceed total pages
  const offset = (currentPage - 1) * itemsPerPage;

  // Handler to change page size and reset to page 1
  const handleSetItemsPerPage = value => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const {
    data,
    loading,
    error: fetchError,
  } = useGet(
    `${ANNUALIZED_MFR_GMV_PRODUCT_MARKET_SHARE}/${productId}`,
    {
      limit: itemsPerPage,
      offset: offset,
    },
    !productId,
    false,
  );

  const error = fetchError?.response?.data?.message || fetchError?.message;

  const formatGMV = value => {
    return `$${Number(value.toFixed(0)).toLocaleString('en-US')}`;
  };

  const formatPercentage = value => {
    return `${value.toFixed(2)}%`;
  };

  return (
    <div className={css(styles.container)}>
      {/* Header */}
      <div className={css(styles.header)}>
        <h2 className={css(styles.title)}>Market Share Analysis</h2>
      </div>

      {loading && (
        <div className={css(styles.loadingContainer)}>
          <Spinner animation="border" variant="primary" />
          <div className={css(styles.loadingText)}>Loading market share data...</div>
        </div>
      )}

      {error && (
        <div className={css(styles.errorContainer)}>
          <div className={css(styles.errorIcon)}>⚠️</div>
          <div className={css(styles.errorText)}>{error}</div>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Stats Header */}
          <div className={css(styles.statsSection)}>
            <div className={css(styles.categoryBadge)}>
              <span className={css(styles.categoryLabel)}>L4 Category:</span>
              <span className={css(styles.categoryName)}>{data.l4_category_name || 'Unknown'}</span>
            </div>

            <div className={css(styles.statsRow)}>
              <div className={css(styles.statBox)}>
                <div className={css(styles.statLabel)}>Market Share</div>
                <div className={css(styles.statValue, styles.marketShareValue)}>
                  {formatPercentage(data.product_market_share)}
                </div>
              </div>

              <div className={css(styles.statBox)}>
                <div className={css(styles.statLabel)}>Product GMV</div>
                <div className={css(styles.statValue)}>{formatGMV(data.product_gmv)}</div>
              </div>

              <div className={css(styles.statBox)}>
                <div className={css(styles.statLabel)}>Category GMV</div>
                <div className={css(styles.statValue)}>{formatGMV(data.l4_total_gmv)}</div>
              </div>
            </div>
          </div>

          {/* Pagination Top */}
          {data?.pagination?.total_count > 0 && (
            <div className={css(styles.paginationContainer)}>
              <PaginationComponent
                totalItems={data.pagination.total_count}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={handleSetItemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                disabled={loading}
              />
            </div>
          )}

          {/* Products Table */}
          {data.competing_products && data.competing_products.length > 0 && (
            <div className={css(styles.tableWrapper)}>
              <Table hover className={css(styles.table)}>
                <thead>
                  <tr>
                    <th className={css(styles.tableHeaderCell)}>Rank</th>
                    <th className={css(styles.tableHeaderCell)}>Product</th>
                    <th className={css(styles.tableHeaderCell)}>SKU</th>
                    <th className={css(styles.tableHeaderCell)}>Pack Size</th>
                    <th className={css(styles.tableHeaderCell, styles.textRight)}>Market Share</th>
                    <th className={css(styles.tableHeaderCell, styles.textRight)}>GMV</th>
                    <th className={css(styles.tableHeaderCell)}>Brand</th>
                    <th className={css(styles.tableHeaderCell)}>Manufacturer</th>
                  </tr>
                </thead>
                <tbody>
                  {data.competing_products.map((product, index) => {
                    const globalRank = offset + index + 1;
                    return (
                      <tr
                        key={product.id}
                        className={css(
                          styles.tableRow,
                          product.is_current_product && styles.currentProductRow,
                        )}
                      >
                        <td className={css(styles.tableCell, styles.rankCell)}>
                          <div className={css(styles.rankContent)}>{globalRank}</div>
                        </td>
                        <td className={css(styles.tableCell)}>
                          <div className={css(styles.productCell)}>
                            <img
                              src={product.thumbnail_url || placeholderProductImage}
                              alt={product.name}
                              className={css(styles.productImage)}
                            />
                            <div className={css(styles.productName)}>{product.name}</div>
                          </div>
                        </td>
                        <td className={css(styles.tableCell, styles.skuCell)}>
                          {product.manufacturer_sku || '-'}
                        </td>
                        <td className={css(styles.tableCell)}>{product.pack_size || '-'}</td>
                        <td className={css(styles.tableCell, styles.textRight)}>
                          <span
                            className={css(
                              styles.marketShareBadge,
                              product.is_current_product && styles.currentBadge,
                            )}
                          >
                            {formatPercentage(product.market_share)}
                          </span>
                        </td>
                        <td className={css(styles.tableCell, styles.textRight, styles.gmvCell)}>
                          {formatGMV(product.product_gmv)}
                        </td>
                        <td className={css(styles.tableCell)}>{product.brand_name || '-'}</td>
                        <td className={css(styles.tableCell)}>
                          {product.manufacturer_name || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {/* Pagination Bottom */}
          {data?.pagination?.total_count > 0 && (
            <div className={css(styles.paginationContainer, styles.paginationBottom)}>
              <PaginationComponent
                totalItems={data.pagination.total_count}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={handleSetItemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                disabled={loading}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: '24px',
    backgroundColor: white,
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: ghostWhite,
    border: `1px solid ${borderGray}`,
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    color: bodyText,
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: borderGray,
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: bodyText,
    margin: 0,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '16px',
  },
  loadingText: {
    fontSize: '14px',
    color: warmGray,
    fontWeight: '500',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    gap: '12px',
  },
  errorIcon: {
    fontSize: '48px',
  },
  errorText: {
    fontSize: '14px',
    color: '#d32f2f',
    textAlign: 'center',
  },
  statsSection: {
    marginBottom: '24px',
    padding: '20px',
    backgroundColor: ghostWhite,
    borderRadius: '12px',
    border: `1px solid ${borderGray}`,
  },
  categoryBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: white,
    borderRadius: '8px',
    border: `1px solid ${borderGray}`,
    marginBottom: '16px',
  },
  categoryLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: warmGray,
    textTransform: 'uppercase',
  },
  categoryName: {
    fontSize: '14px',
    fontWeight: '700',
    color: softPurple,
  },
  statsRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  },
  statBox: {
    flex: '1 1 200px',
    padding: '16px',
    backgroundColor: white,
    borderRadius: '10px',
    border: `1px solid ${borderGray}`,
  },
  statLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: warmGray,
    textTransform: 'uppercase',
    marginBottom: '8px',
    letterSpacing: '0.5px',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: bodyText,
  },
  marketShareValue: {
    color: softPurple,
    fontSize: '24px',
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20px',
    marginBottom: '20px',
    padding: '12px',
    backgroundColor: ghostWhite,
    borderRadius: '8px',
  },
  paginationBottom: {
    marginTop: '24px',
  },
  tableWrapper: {
    border: `1px solid ${borderGray}`,
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: white,
  },
  table: {
    marginBottom: 0,
  },
  tableHeaderCell: {
    backgroundColor: ghostWhite,
    fontSize: '12px',
    fontWeight: '700',
    color: bodyText,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '14px 16px',
    borderBottom: `2px solid ${borderGray}`,
  },
  tableRow: {
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#f8f9fa',
    },
  },
  currentProductRow: {
    backgroundColor: `${softGreen}10`,
    ':hover': {
      backgroundColor: `${softGreen}20`,
    },
  },
  tableCell: {
    padding: '14px 16px',
    fontSize: '13px',
    color: bodyText,
    verticalAlign: 'middle',
  },
  rankCell: {
    fontWeight: '700',
    fontSize: '14px',
  },
  rankContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  rankIcon: {
    fontSize: '16px',
  },
  productCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  productImage: {
    width: '50px',
    height: '50px',
    objectFit: 'contain',
    borderRadius: '6px',
    border: `1px solid ${borderGray}`,
    padding: '4px',
    backgroundColor: white,
  },
  productName: {
    fontSize: '13px',
    fontWeight: '600',
    color: bodyText,
    lineHeight: '1.4',
  },
  skuCell: {
    fontSize: '12px',
    color: warmGray,
    fontFamily: 'monospace',
  },
  textRight: {
    textAlign: 'right',
  },
  marketShareBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: `${softPurple}15`,
    color: softPurple,
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '700',
  },
  currentBadge: {
    backgroundColor: `${softGreen}20`,
    color: softGreen,
  },
  gmvCell: {
    fontWeight: '600',
  },
});

MarketShareView.propTypes = {
  // productId comes from useParams
};

export default MarketShareView;
